import { CanvasShapeEnum } from 'common/enums/CanvasEnum';
import Icons from 'common/enums/IconEnum';
import ColorUtil from 'common/utils/ColorUtil';

export class CanvasUtil {
    static drawShape({ shape, ...others }) {
        switch (shape) {
            case CanvasShapeEnum.Square:
                CanvasUtil.drawSquare(others);
                break;
            case CanvasShapeEnum.SquareWithArrow:
                CanvasUtil.drawSquareWithArrow(others);
                break;
            case CanvasShapeEnum.Circle:
                CanvasUtil.drawCircleMarker(others);
                break;
            case CanvasShapeEnum.SpiderCircle:
                CanvasUtil.drawSpiderCircleMarker(others);
                break;
            case CanvasShapeEnum.Triangle:
                break;
            default:
                CanvasUtil.drawCircle(others);
                break;
        }
    }

    static drawSquareWithArrow({
        ctx,
        x,
        y,
        color = '#000000',
        borderColor = '#FFFFFF',
        borderWidth = 1,
        sideLength = 20,
        arrowLength = 3,
        opacity = 1,
        stripeColor,
        label,
        minimal,
        labelFontSize,
        labelFontWeight,
        canvasIconFontSize,
        withTextShadow,
        iconColor
    }) {
        if (minimal) {
            CanvasUtil.drawCircle({
                ctx,
                x,
                y,
                radius: 3.6,
                borderWidth: 1.2,
                color
            });
            return;
        }
        const radius = 2;
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        ctx.beginPath();
        ctx.moveTo(x, y);
        const squareBottom =
            y -
            Math.sqrt(
                arrowLength * arrowLength -
                    (arrowLength / 2) * (arrowLength / 2)
            );
        ctx.lineTo(x - Math.ceil(arrowLength / 2), squareBottom); //completed left edge of arrow

        ctx.lineTo(x - sideLength / 2 + radius, squareBottom);
        ctx.quadraticCurveTo(
            x - sideLength / 2,
            squareBottom,
            x - sideLength / 2,
            squareBottom - radius
        );
        ctx.lineTo(x - sideLength / 2, squareBottom - sideLength + radius);
        ctx.quadraticCurveTo(
            x - sideLength / 2,
            squareBottom - sideLength,
            x - sideLength / 2 + radius,
            squareBottom - sideLength
        );
        ctx.lineTo(x + sideLength / 2 - radius, squareBottom - sideLength);
        ctx.quadraticCurveTo(
            x + sideLength / 2,
            squareBottom - sideLength,
            x + sideLength / 2,
            squareBottom - sideLength + radius
        );
        ctx.lineTo(x + sideLength / 2, squareBottom - radius);
        ctx.quadraticCurveTo(
            x + sideLength / 2,
            squareBottom,
            x + sideLength / 2 - radius,
            squareBottom
        );
        ctx.lineTo(x + Math.ceil(arrowLength / 2), squareBottom);
        ctx.lineTo(x, y);
        ctx.closePath();

        //Fill color

        if (Array.isArray(color)) {
            if (color.length !== 2) {
                throw new Error(
                    `Exactly 2 colors expected, found ${color.length}`
                );
            }
            const gradient = ctx.createLinearGradient(
                x - sideLength / 2,
                y,
                x + sideLength / 2,
                y
            );
            gradient.addColorStop(0.4, color[0]);
            gradient.addColorStop(0.6, color[1]);
            ctx.fillStyle = gradient;
        } else if (stripeColor) {
            const stripe = document.createElement('canvas');
            stripe.width = 16;
            stripe.height = 8;
            const stripeCtx = stripe.getContext('2d');

            // Fill regular background color
            stripeCtx.beginPath();
            stripeCtx.rect(0, 0, 16, 8);
            stripeCtx.fillStyle = color;
            stripeCtx.fill();

            // Draw colored stripes
            const x0 = -2;
            const x1 = 18;
            const y0 = -1;
            const y1 = 9;
            const offset = 16;
            stripeCtx.strokeStyle = stripeColor;
            stripeCtx.lineWidth = 4;
            stripeCtx.beginPath();
            stripeCtx.moveTo(x0, y0);
            stripeCtx.lineTo(x1, y1);
            stripeCtx.moveTo(x0 - offset, y0);
            stripeCtx.lineTo(x1 - offset, y1);
            stripeCtx.moveTo(x0 + offset, y0);
            stripeCtx.lineTo(x1 + offset, y1);
            stripeCtx.stroke();
            stripeCtx.closePath();
            ctx.fillStyle = ctx.createPattern(stripe, 'repeat');
        } else {
            ctx.fillStyle = color;
        }
        ctx.globalAlpha = opacity;
        ctx.fill();
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 10;
        ctx.stroke();
        ctx.shadowColor = '';
        ctx.shadowBlur = 0;
        CanvasUtil.drawLabel({
            ctx,
            label,
            x,
            y: squareBottom - sideLength / 2,
            canvasIconFontSize,
            labelFontSize,
            labelFontWeight,
            withTextShadow,
            iconColor
        });

        return ctx;
    }

    static drawSquare({
        ctx,
        x,
        y,
        color = '#000000',
        borderColor = '#FFFFFF',
        borderWidth = 2,
        sideLength = 20,
        label,
        minimal,
        canvasIconFontSize
    }) {
        const newSideLength = sideLength + borderWidth;
        if (minimal) {
            CanvasUtil.drawCircle({
                ctx,
                x,
                y,
                radius: 3.6,
                borderWidth: 1.2,
                color
            });
        } else {
            CanvasUtil.drawRoundRect({
                ctx,
                x: x - newSideLength / 2,
                y: y - newSideLength / 2,
                width: newSideLength,
                height: newSideLength,
                borderWidth,
                fill: color,
                stroke: borderColor
            });
            CanvasUtil.drawLabel({
                ctx,
                label,
                x,
                y,
                canvasIconFontSize
            });
        }
        return ctx;
    }

    static drawRoundRect({
        ctx,
        x,
        y,
        width,
        height,
        borderWidth,
        radius = 1,
        fill = '#000',
        stroke = '#fff'
    }) {
        ctx.clearRect(x, y, width, height);
        ctx.strokeStyle = stroke;
        ctx.fillStyle = fill;
        ctx.lineWidth = borderWidth;
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(
            x + width,
            y + height,
            x + width - radius,
            y + height
        );
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 10;
        ctx.stroke();
        ctx.shadowColor = '';
        ctx.shadowBlur = 0;

        return ctx;
    }
    static drawLabel({
        ctx,
        iconColor,
        label,
        x,
        y,
        withTextShadow = false,
        canvasIconFontSize = 12,
        labelFontWeight = 600,
        labelFontSize = 8
    }) {
        if (label) {
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = iconColor || '#FFF';
            let newLabel = label;
            if (withTextShadow) {
                ctx.shadowColor = 'rgb(0,0,0)';
                ctx.shadowBlur = 2;
            }
            if (isNaN(label)) {
                ctx.font = `400 ${canvasIconFontSize}px icomoon, serif`;
                newLabel = Icons[label].unicode;
            } else {
                ctx.font = `${labelFontWeight} ${labelFontSize}px "Open Sans", sans-serif`;
            }
            ctx.fillText(newLabel, x, y);
        }
        return ctx;
    }

    static drawSpiderCircleMarker({
        ctx,
        x,
        y,
        borderColor,
        color,
        iconColor,
        isDisabled,
        radius,
        borderWidth,
        label,
        canvasIconFontSize
    }) {
        if (isDisabled) {
            CanvasUtil.drawCircle({
                ctx,
                x,
                y,
                radius: 3.6,
                borderWidth: 1.2,
                color
            });
            return;
        }
        const spiderBorderColor = ColorUtil.hexToRgbAString({
            color,
            opacity: 0.3
        });

        CanvasUtil.drawCircle({
            ctx,
            x,
            y,
            shadow: false,
            borderColor: spiderBorderColor,
            color: 'rgba(0,0,0,0)',
            radius,
            borderWidth: 6
        });
        CanvasUtil.drawCircleMarker({
            ctx,
            x,
            y,
            shadow: false,
            borderColor,
            iconColor,
            color,
            radius,
            borderWidth,
            label,
            canvasIconFontSize
        });
    }

    static drawCircleMarker({
        ctx,
        x,
        y,
        shadow,
        shadowColor,
        borderColor,
        iconColor,
        color,
        radius,
        borderWidth,
        label,
        canvasIconFontSize
    }) {
        CanvasUtil.drawCircle({
            ctx,
            x,
            y,
            shadow,
            shadowColor,
            borderColor,
            color,
            radius,
            borderWidth
        });
        if (label) {
            CanvasUtil.drawLabel({
                ctx,
                label,
                iconColor,
                x,
                y,
                canvasIconFontSize
            });
        }
    }

    static drawCircle({
        ctx,
        x,
        y,
        shadow = true,
        shadowColor = 'rgba(0, 0, 0, 0.1)',
        borderColor = '#FFFFFF',
        color = '#00b92a',
        radius = 5,
        borderWidth = 2
    }) {
        if (shadow) {
            ctx.fillStyle = shadowColor;
            ctx.beginPath();
            ctx.arc(x, y, radius + borderWidth + 1, 0, Math.PI * 2, true);
            ctx.closePath();
            ctx.fill();
        }
        ctx.fillStyle = borderColor;
        ctx.beginPath();
        ctx.arc(x, y, radius + borderWidth, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();

        if (Array.isArray(color)) {
            if (color.length !== 2) {
                throw new Error(
                    `Exactly 2 colors expected, found ${color.length}`
                );
            }
            const gradient = ctx.createLinearGradient(
                x - radius / 2,
                y - radius / 2,
                x + radius / 2,
                y + radius / 2
            );
            gradient.addColorStop(0.4, color[0]);
            gradient.addColorStop(0.6, color[1]);
            ctx.fillStyle = gradient;
        } else {
            ctx.fillStyle = color;
        }
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.fill();
        return ctx;
    }
}
export default CanvasUtil;
